import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { NewFieldVisibility } from '../../../../../shared/enums/new-field-visibility.enum';
import { FIELD_TYPES } from '../../../../../shared/fields/field-types';
import * as rootSelectors from '../../../core/store';
import * as fieldActions from '../../../core/store/fields/field.actions';
import { Department, Field } from '../../../core/store/models';
import { ActionStatus } from '../../../core/store/types';
import { buildPDropdownOptions } from '../../../core/store/utils';
import { PDropdownOption } from '../../interfaces/p-dropdown-options';
import { multiPicklistFilterChange } from '../../../core/store/utils';

export enum FieldModalTypes {
    Create,
    Edit
};

export interface FieldModalData {
    departments: Department[];
    studioId: number;
    field?: Field;
}

@Component({
    templateUrl: './field-modal.component.html',
    styleUrls: ['./field-modal.component.scss'],
    standalone: false
})
export class FieldModalComponent implements OnInit {
    canonicalNameField = {
        canonical_name: 'canonical_name',
        type: FIELD_TYPES.SHORT_TEXT_AREA.type,
        name: 'Canonical Name',
        max_length: 100
    };
    departmentOptions: PDropdownOption[] = [];
    departmentsToAddField = {
        canonical_name: 'departments',
        type: FIELD_TYPES.MULTI_PICKLIST.type,
        name: 'Departments to Add to'
    };
    fieldTypeField = {
        canonical_name: 'type',
        type: FIELD_TYPES.PICKLIST.type,
        name: 'Field Type'
    };
    field: Field;
    FieldModalTypes = FieldModalTypes;
    fieldTypesOptions: PDropdownOption[] = [];
    form: UntypedFormGroup;
    modalType: FieldModalTypes;
    nameField = {
        name: 'Name',
        canonical_name: 'name',
        type: FIELD_TYPES.SHORT_TEXT_AREA.type,
    };
    saving = false;
    sortOrderField = {
        canonical_name: 'sort_order',
        type: FIELD_TYPES.NUMBER.type,
        name: 'Sort Order'
    };
    visibilityField = {
        canonical_name: 'visibility',
        type: FIELD_TYPES.PICKLIST.type,
        name: 'Visibility'
    };
    visibilityOptions: PDropdownOption[] = [
        { label: NewFieldVisibility[0], value: NewFieldVisibility.All },
        { label: NewFieldVisibility[1], value: NewFieldVisibility.Feature },
        { label: NewFieldVisibility[2], value: NewFieldVisibility.Division }
    ];

    heightPixel: number = 5;
    constructor(@Inject(MAT_DIALOG_DATA) private data: FieldModalData, public dialogRef: MatDialogRef<FieldModalComponent>, public fb: UntypedFormBuilder, private store: Store<rootSelectors.State>) { }

    ngOnInit() {
        this.fieldTypesOptions = this.buildFieldTypesDropdownOptions();
        this.departmentOptions = buildPDropdownOptions(this.data.departments);
        this.modalType = this.data.field ? FieldModalTypes.Edit : FieldModalTypes.Create;
        this.field = this.data.field;
        this.buildForm();
    }

    // Manually updating the logic for choose columns adjusting height. 
    // We have to re-verify the fix was done on Primeng and remove this logic
    onFilterChange(event: any) {
        const query = event.filter?.toLowerCase() || '';
        if (query) {
            this.heightPixel = multiPicklistFilterChange(query, this.departmentOptions)
        } else {
            this.heightPixel = this.departmentOptions.length;
        }
    }
    buildFieldTypesDropdownOptions(): PDropdownOption[] {
        if (this.modalType === FieldModalTypes.Create) {
            const enabledFieldTypes = [FIELD_TYPES.SHORT_TEXT_AREA.type, FIELD_TYPES.LONG_TEXT_AREA.type, FIELD_TYPES.CHECKBOX.type, FIELD_TYPES.EMAIL.type, FIELD_TYPES.PHONE.type, FIELD_TYPES.DATE.type, FIELD_TYPES.NUMBER.type, FIELD_TYPES.URL.type, FIELD_TYPES.CURRENCY.type];
            return enabledFieldTypes.map(type => ({
                label: type,
                value: type
            }));
        } else {
            // The field type dropdown is disabled in Edit mode, this is just for displaying the current field type
            return Object.keys(FIELD_TYPES).map(key => ({
                label: FIELD_TYPES[key].type,
                value: FIELD_TYPES[key].type
            }));
        }
    }

    buildForm() {
        const formObj: { [formControlName: string]: any } = {
            [this.nameField.canonical_name]: [this.field ? this.field.name : '', [Validators.required, Validators.maxLength(100)]],
            [this.visibilityField.canonical_name]: [this.field ? this.field.visibility : '', Validators.required],
            [this.canonicalNameField.canonical_name]: [this.field ? this.field.canonical_name : '', [Validators.required, Validators.pattern(/[a-z | _]+/g), Validators.maxLength(100)]],
            [this.fieldTypeField.canonical_name]: [this.field ? this.field.type : '', Validators.required],
            [this.departmentsToAddField.canonical_name]: [this.field ? this.field.departmentFields.map(df => df.department_id) : '', Validators.required],
            [this.sortOrderField.canonical_name]: [this.field ? this.field.sort_order : '', [Validators.required, Validators.min(-2147483648), Validators.max(2147483647)]],
            restricted: [this.field ? this.field.restricted : false],
            required_in_all_depts: [this.field && this.field.departmentFields[0] ? !this.field.departmentFields.some(dp => !dp.required) : false]
        };

        this.form = this.fb.group(formObj);
    }

    handleNameInput() {
        const nameControl: AbstractControl = this.form.get('name');
        let nameValue: string;

        if (nameControl) {
            nameValue = nameControl.value;
        }

        if (this.modalType === FieldModalTypes.Create) {
            // Replace all spaces with underscores, remove all special characters besides underscore, lowercase
            this.form.patchValue({ canonical_name: nameValue.replace(/ /g, '_').replace(/\W+/g, '').toLowerCase() });
        }
    }

    onSave() {
        const values = this.form.getRawValue();
        const departmentIds: number[] = values.departments;
        const requiredInAllDepts: boolean = values.required_in_all_depts;
        delete values.departments;
        delete values.required_in_all_depts;
        const newField: Field = new Field({
            ...values,
            studio_id: this.data.studioId,
            delete_locked: false,
            required_locked: false
        });

        this.saving = true;
        if (this.modalType === FieldModalTypes.Create) {
            this.store.dispatch(new fieldActions.CreateAction({
                studioId: newField.studio_id,
                field: newField,
                deptIds: departmentIds,
                requiredInAllDepts
            }));
        } else {
            this.store.dispatch(new fieldActions.UpdateAction({
                studioId: newField.studio_id,
                fieldId: this.field.id,
                field: newField,
                deptIds: departmentIds,
                requiredInAllDepts
            }));
        }

        this.store.select(rootSelectors.getFieldSaving).pipe(filter(s => s !== ActionStatus.Loading), take(1)).subscribe(status => {
            this.saving = false;

            if (status === ActionStatus.Complete) {
                this.dialogRef.close(true);
            } else {
                this.dialogRef.close(false);
            }
        });
    }
}
